// src/pages/kanban/KanbanPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Badge, Spinner } from '@/components/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import {
  Plus,
  Star,
  Eye,
  Edit2,
  Copy,
  Trash2,
  MoreHorizontal,
  LayoutGrid,
  Share2,
  Ticket,
  CheckSquare,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  useTableros,
  useDeleteTablero,
  useSetTableroDefault,
  useDuplicarTablero,
} from '@/hooks/queries/useKanban';
import { TableroFormModal } from './components/TableroFormModal';
import { TipoTableroKanban, AgrupacionKanban } from '@/types/kanban';
import type { Tablero } from '@/types/kanban';

const AGRUPACION_LABELS: Record<AgrupacionKanban, string> = {
  [AgrupacionKanban.ESTADO]: 'Estado',
  [AgrupacionKanban.PRIORIDAD]: 'Prioridad',
  [AgrupacionKanban.ASIGNADO]: 'Asignado',
  [AgrupacionKanban.CLIENTE]: 'Cliente',
  [AgrupacionKanban.TIPO]: 'Tipo',
  [AgrupacionKanban.CATEGORIA]: 'Categoria',
  [AgrupacionKanban.MODULO_ERP]: 'Modulo ERP',
};

export function KanbanPage() {
  const navigate = useNavigate();
  const [tipoSeleccionado, setTipoSeleccionado] = useState<TipoTableroKanban>(
    TipoTableroKanban.TICKETS
  );
  const { data: tableros, isLoading } = useTableros(tipoSeleccionado);
  const deleteMutation = useDeleteTablero();
  const setDefaultMutation = useSetTableroDefault();
  const duplicarMutation = useDuplicarTablero();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTablero, setEditingTablero] = useState<Tablero | undefined>();

  const handleView = (id: string) => {
    navigate(`/kanban/${id}`);
  };

  const handleEdit = (tablero: Tablero) => {
    setEditingTablero(tablero);
    setIsModalOpen(true);
  };

  const handleDuplicate = async (tablero: Tablero) => {
    const nuevoNombre = `${tablero.nombre} (copia)`;
    await duplicarMutation.mutateAsync({ id: tablero.id, nombre: nuevoNombre });
  };

  const handleSetDefault = async (id: string) => {
    await setDefaultMutation.mutateAsync(id);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Estas seguro de que deseas eliminar este tablero?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTablero(undefined);
  };

  const handleCreateNew = () => {
    setEditingTablero(undefined);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tableros Kanban</h1>
          <p className="text-gray-500">Visualiza y gestiona tu trabajo con tableros Kanban</p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Tablero
        </Button>
      </div>

      {/* Tabs por tipo */}
      <Tabs
        value={tipoSeleccionado}
        onValueChange={(value) => setTipoSeleccionado(value as TipoTableroKanban)}
      >
        <TabsList>
          <TabsTrigger value={TipoTableroKanban.TICKETS} className="gap-2">
            <Ticket className="h-4 w-4" />
            Tickets
          </TabsTrigger>
          <TabsTrigger value={TipoTableroKanban.ACTIVIDADES} className="gap-2">
            <CheckSquare className="h-4 w-4" />
            Actividades
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tipoSeleccionado}>
          {/* Content */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : !tableros || tableros.length === 0 ? (
            <Card className="p-12">
              <div className="text-center">
                <LayoutGrid className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No tienes tableros de {tipoSeleccionado === TipoTableroKanban.TICKETS ? 'tickets' : 'actividades'}
                </h3>
                <p className="text-gray-500 mb-4">
                  Crea tu primer tablero Kanban para visualizar y organizar tu trabajo
                </p>
                <Button onClick={handleCreateNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear tablero
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tableros.map((tablero) => (
                <Card
                  key={tablero.id}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleView(tablero.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {tablero.nombre}
                        </h3>
                        {tablero.es_default && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                        {tablero.es_compartido && (
                          <Share2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      {tablero.descripcion && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {tablero.descripcion}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => handleView(tablero.id)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Abrir
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(tablero)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(tablero)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicar
                        </DropdownMenuItem>
                        {!tablero.es_default && (
                          <DropdownMenuItem onClick={() => handleSetDefault(tablero.id)}>
                            <Star className="h-4 w-4 mr-2" />
                            Establecer como default
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDelete(tablero.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <Badge variant="default">
                      {AGRUPACION_LABELS[tablero.agrupacion]}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {tablero.updated_at
                        ? format(new Date(tablero.updated_at), "d MMM yyyy", { locale: es })
                        : format(new Date(tablero.created_at), "d MMM yyyy", { locale: es })}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <TableroFormModal
        open={isModalOpen}
        onClose={handleCloseModal}
        tablero={editingTablero}
        tipoDefault={tipoSeleccionado}
      />
    </div>
  );
}
